import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Rating,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Star,
  MessageOutlined,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import serviceRequestService from '../../services/serviceRequestService';
import moment from 'moment';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    averageRating: 0,
    fiveStar: 0,
    fourStar: 0,
    threeStar: 0,
    twoStar: 0,
    oneStar: 0,
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const data = await serviceRequestService.getReviews();
      setReviews(Array.isArray(data) ? data : []);
      console.log(data);
      
      // Calculate stats
      const total = data.length;
      const getRating = (r) => Number(r.userReview?.customer?.rating) || 0;
      const avgRating = total > 0
        ? data.reduce((sum, r) => sum + getRating(r), 0) / total
        : 0;

      const ratingCounts = {
        fiveStar:  data.filter(r => getRating(r) === 5).length,
        fourStar:  data.filter(r => getRating(r) === 4).length,
        threeStar: data.filter(r => getRating(r) === 3).length,
        twoStar:   data.filter(r => getRating(r) === 2).length,
        oneStar:   data.filter(r => getRating(r) === 1).length,
      };

      setStats({
        total,
        averageRating: avgRating,
        ...ratingCounts,
      });
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Customer Reviews
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Track customer feedback and ratings for your partners' services
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Total Reviews</Typography>
              <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ borderLeft: '4px solid #ffc107' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">Average Rating</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                <Typography variant="h3" fontWeight="bold" sx={{ mr: 1 }}>
                  {stats.averageRating.toFixed(1)}
                </Typography>
                <Star sx={{ color: '#ffc107', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7.2}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Rating Distribution
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={2.4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      icon={<Star />}
                      label="5"
                      size="small"
                      color="success"
                    />
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {stats.fiveStar}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      icon={<Star />}
                      label="4"
                      size="small"
                      color="primary"
                    />
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {stats.fourStar}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      icon={<Star />}
                      label="3"
                      size="small"
                      color="info"
                    />
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {stats.threeStar}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      icon={<Star />}
                      label="2"
                      size="small"
                      color="warning"
                    />
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {stats.twoStar}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={2.4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      icon={<Star />}
                      label="1"
                      size="small"
                      color="error"
                    />
                    <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                      {stats.oneStar}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reviews Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Booking ID</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Customer Review</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">No reviews found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((request) => (
                <TableRow key={request.serviceRequestId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={request.userReview?.customer?.profilePicture}
                        sx={{ width: 40, height: 40 }}
                      >
                        {request.customerName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {request.customerName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {request.mobileNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request.service?.name || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{request.address}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={request.bookingId || 'N/A'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating
                        value={Number(request.userReview?.customer?.rating) || 0}
                        readOnly
                        size="small"
                      />
                      <Typography variant="body2" fontWeight="bold">
                        ({Number(request.userReview?.customer?.rating) || 0})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                      {request.userReview?.customer && (
                        <>
                          <MessageOutlined sx={{ fontSize: 16, color: 'text.secondary', mt: 0.5 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {request.userReview.customer?.comment}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(request.serviceDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Reviews;
